// Pure helpers for node-anchored comments — mapping a stored (nodeKind, nodeId)
// to a diff EntityRef, deriving live "outdated" from the ChangeSet, labelling,
// and placing pins from measured node rects. No DOM/React here so it's
// unit-testable; the overlay component does the measuring and calls in.
import type { DiffLookup, EntityRef } from "@archlang/engine";
import type { ReviewComment } from "./proposalsApi.js";

/** Board-anchorable node kinds (id-keyed EntityRefs). Interface anchors need a
 *  moduleId+name pair the board DOM doesn't expose, so they're out of Phase A. */
const ID_KINDS = new Set(["module", "process", "subprocess", "view"]);

/** Build an EntityRef for a stored node anchor, or null if unsupported. */
export function toEntityRef(nodeKind: string | null, nodeId: string | null): EntityRef | null {
  if (!nodeKind || !nodeId || !ID_KINDS.has(nodeKind)) return null;
  return { kind: nodeKind, id: nodeId } as EntityRef;
}

/**
 * A node-anchored comment is "outdated" when its node was removed by the change
 * (derived live from the ChangeSet, never stored). No lookup / non-node anchor
 * / unsupported kind → not outdated.
 */
export function nodeCommentOutdated(lookup: DiffLookup | null, comment: ReviewComment): boolean {
  if (!lookup || comment.anchorKind !== "node") return false;
  const ref = toEntityRef(comment.nodeKind, comment.nodeId);
  if (!ref) return false;
  return lookup.entityStatus(ref) === "removed";
}

/** Short human label for a node chip: "module · Billing". */
export function shortNodeLabel(nodeKind: string | null, nodeId: string | null): string {
  const seg = (nodeId ?? "").split(/::|\./).filter(Boolean).pop() ?? nodeId ?? "node";
  return `${nodeKind ?? "node"} · ${seg}`;
}

export interface RectLike {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface NodePin {
  readonly nodeId: string;
  readonly nodeKind: string | null;
  readonly x: number;
  readonly y: number;
  readonly comments: ReviewComment[];
}

/**
 * Place one pin per anchored node that has a measured rect, aggregating all its
 * root threads. Coordinates are relative to whatever the rects are relative to
 * (the overlay measures node rects against the board host). The pin sits at the
 * node's top-right corner.
 */
export function placePins(
  rectsById: ReadonlyMap<string, RectLike>,
  roots: readonly ReviewComment[],
): NodePin[] {
  const byNode = new Map<string, ReviewComment[]>();
  for (const c of roots) {
    if (c.anchorKind !== "node" || !c.nodeId) continue;
    if (!rectsById.has(c.nodeId)) continue;
    const list = byNode.get(c.nodeId) ?? [];
    list.push(c);
    byNode.set(c.nodeId, list);
  }
  const pins: NodePin[] = [];
  for (const [nodeId, comments] of byNode) {
    const r = rectsById.get(nodeId)!;
    pins.push({
      nodeId,
      nodeKind: comments[0].nodeKind,
      x: r.left + r.width - 6,
      y: r.top - 6,
      comments,
    });
  }
  return pins;
}
